import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import DialogModal from '../components/DialogModal';
import DocumentsPanel from '../components/DocumentsPanel';
import staffService from '../services/staffService';
import { ArrowLeft, Phone, Envelope, Briefcase, MapPin, Calendar, User, GraduationCap, Building, Pencil, Check, X, Eye, EyeSlash, CircleNotch } from '@phosphor-icons/react';

function StaffDetailPage() {
  const { staffId } = useParams();
  const navigate = useNavigate();

  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});
  const [managerOptions, setManagerOptions] = useState([]);
  const [regeneratingStaff, setRegeneratingStaff] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [probationSaving, setProbationSaving] = useState(false);
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    primaryAction: null,
    secondaryAction: null
  });

  // Dialog helpers
  const showDialog = (config) => {
    setDialogConfig({
      isOpen: true,
      ...config
    });
  };

  const closeDialog = () => {
    setDialogConfig(prev => ({
      ...prev,
      isOpen: false
    }));
  };

  // Load staff details
  const loadStaffDetails = useCallback(() => {
    setLoading(true);
    staffService.getStaff(staffId)
      .then(data => {
        setStaff(data);
        setFormData({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          phone: data.phone || '',
          date_of_birth: data.date_of_birth || '',
          gender: data.gender || 'male',
          staff_type: data.staff_type || 'teaching',
          position: data.position || '',
          department: data.department || '',
          qualification: data.qualification || '',
          date_joined: data.date_joined || '',
          manager_id: data.manager_id || ''
        });
      })
      .catch(err => {
        toast.error(err.message || 'Failed to load staff details');
        navigate('/staff');
      })
      .finally(() => setLoading(false));
  }, [staffId, navigate]);

  useEffect(() => {
    loadStaffDetails();
  }, [loadStaffDetails]);

  useEffect(() => {
    if (editing && managerOptions.length === 0) {
      staffService.listStaff({ limit: 100 })
        .then(data => setManagerOptions((data?.items || data) || []))
        .catch(() => {});
    }
  }, [editing, managerOptions.length]);

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setFormData({
      first_name: staff.first_name || '',
      last_name: staff.last_name || '',
      email: staff.email || '',
      phone: staff.phone || '',
      date_of_birth: staff.date_of_birth || '',
      gender: staff.gender || 'male',
      staff_type: staff.staff_type || 'teaching',
      position: staff.position || '',
      department: staff.department || '',
      qualification: staff.qualification || '',
      date_joined: staff.date_joined || '',
      manager_id: staff.manager_id || ''
    });
    setEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Include staff_id in the request body
      const updateData = {
        ...formData,
        staff_id: staff.staff_id
      };
      await staffService.updateStaff(staffId, updateData);
      toast.success('Staff details updated successfully!');
      setEditing(false);
      loadStaffDetails(); // Reload to get updated data
    } catch (error) {
      toast.error(error.message || 'Failed to update staff details');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRegenerateStaffCredentials = async () => {
    showDialog({
      title: 'Regenerate Staff Portal Credentials',
      message: 'Are you sure you want to regenerate this staff member\'s portal credentials? This will create new login information.',
      type: 'warning',
      primaryAction: {
        label: 'Regenerate',
        onClick: async () => {
          closeDialog();
          try {
            setRegeneratingStaff(true);
            const result = await staffService.regeneratePortalCredentials(staffId);
            toast.success('Staff portal credentials regenerated successfully');
            
            // Update the staff state with new credentials
            setStaff(prev => ({
              ...prev,
              portal_account: result.portal_account
            }));
          } catch (error) {
            toast.error(error.message || 'Failed to regenerate staff credentials');
          } finally {
            setRegeneratingStaff(false);
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  // ---- Probation ----

  const handleStartProbation = () => {
    showDialog({
      title: 'Start Probation',
      message: 'Set the probation end date for this staff member.',
      type: 'info',
      input: { label: 'Probation end date', placeholder: 'YYYY-MM-DD', defaultValue: '', required: true },
      primaryAction: {
        label: 'Start Probation',
        onClick: async (endDate) => {
          try {
            setProbationSaving(true);
            await staffService.startProbation(staffId, endDate);
            toast.success('Probation started');
            loadStaffDetails();
          } catch (error) {
            toast.error(error.message || 'Failed to start probation');
          } finally {
            setProbationSaving(false);
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  const handleConfirmProbation = () => {
    showDialog({
      title: 'Confirm Probation',
      message: 'Confirm this staff member as having passed probation?',
      type: 'success',
      primaryAction: {
        label: 'Confirm',
        onClick: async () => {
          try {
            setProbationSaving(true);
            await staffService.decideProbation(staffId, 'confirmed');
            toast.success('Probation confirmed');
            loadStaffDetails();
          } catch (error) {
            toast.error(error.message || 'Failed to confirm probation');
          } finally {
            setProbationSaving(false);
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  const handleExtendProbation = () => {
    showDialog({
      title: 'Extend Probation',
      message: 'Set the new probation end date.',
      type: 'warning',
      input: { label: 'New probation end date', placeholder: 'YYYY-MM-DD', defaultValue: staff?.probation_end_date || '', required: true },
      primaryAction: {
        label: 'Extend Probation',
        onClick: async (newEndDate) => {
          try {
            setProbationSaving(true);
            await staffService.decideProbation(staffId, 'extended', newEndDate);
            toast.success('Probation extended');
            loadStaffDetails();
          } catch (error) {
            toast.error(error.message || 'Failed to extend probation');
          } finally {
            setProbationSaving(false);
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  const handleTerminateProbation = () => {
    showDialog({
      title: 'Terminate Probation',
      message: 'Are you sure you want to terminate this staff member\'s probation? This marks probation as failed.',
      type: 'error',
      primaryAction: {
        label: 'Terminate',
        onClick: async () => {
          try {
            setProbationSaving(true);
            await staffService.decideProbation(staffId, 'terminated');
            toast.success('Probation terminated');
            loadStaffDetails();
          } catch (error) {
            toast.error(error.message || 'Failed to terminate probation');
          } finally {
            setProbationSaving(false);
          }
        }
      },
      secondaryAction: {
        label: 'Cancel',
        onClick: closeDialog
      }
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading staff details...</div>
      </Layout>
    );
  }

  if (!staff) {
    return (
      <Layout>
        <div className="empty-state">Staff member not found</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        {/* Header */}
        <div className="page-header">
          <div className="header-with-back">
            <button
              className="btn-icon"
              onClick={() => navigate('/staff')}
              title="Go back"
            >
              <ArrowLeft size={20} weight="bold" />
            </button>
            <div>
              <h1>{staff.first_name} {staff.last_name}</h1>
              <p>Staff ID: {staff.staff_id}</p>
            </div>
          </div>
          <div className="header-actions">
            {!editing ? (
              <button className="btn btn-secondary" onClick={handleEdit}>
                <Pencil size={16} weight="bold" /> Edit Details
              </button>
            ) : (
              <div className="edit-actions">
                <button 
                  className="btn btn-secondary" 
                  onClick={handleCancel}
                  disabled={saving}
                >
                  <X size={16} weight="bold" /> Cancel
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? (
                    <span className="button-loading">
                      <CircleNotch size={16} weight="fill" className="spinner" />
                      Saving...
                    </span>
                  ) : (
                    <><Check size={16} weight="bold" /> Save</>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Staff Info Cards */}
        <div className="staff-info-grid">
          <div className="info-card">
            <h3>Staff Information</h3>
            <div className="info-rows">
              <div className="info-row">
                <span className="label">Staff ID:</span>
                <span className="value mono">{staff.staff_id}</span>
              </div>
              <div className={`info-row ${editing ? 'editing' : ''}`}>
                <span className="label">First Name:</span>
                {editing ? (
                  <input
                    type="text"
                    className="form-input-sm"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange('first_name', e.target.value)}
                    disabled={saving}
                    required
                  />
                ) : (
                  <span className="value">{staff.first_name}</span>
                )}
              </div>
              <div className={`info-row ${editing ? 'editing' : ''}`}>
                <span className="label">Last Name:</span>
                {editing ? (
                  <input
                    type="text"
                    className="form-input-sm"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange('last_name', e.target.value)}
                    disabled={saving}
                    required
                  />
                ) : (
                  <span className="value">{staff.last_name}</span>
                )}
              </div>
              <div className={`info-row ${editing ? 'editing' : ''}`}>
                <span className="label">Date of Birth:</span>
                {editing ? (
                  <input
                    type="date"
                    className="form-input-sm"
                    value={formData.date_of_birth}
                    onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                    disabled={saving}
                  />
                ) : (
                  <span className="value">{staff.date_of_birth || '-'}</span>
                )}
              </div>
              <div className={`info-row ${editing ? 'editing' : ''}`}>
                <span className="label">Gender:</span>
                {editing ? (
                  <select
                    className="form-select-sm"
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                    disabled={saving}
                  >
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                ) : (
                  <span className="value capitalize">{staff.gender || '-'}</span>
                )}
              </div>
              <div className={`info-row ${editing ? 'editing' : ''}`}>
                <span className="label">Staff Type:</span>
                {editing ? (
                  <select
                    className="form-select-sm"
                    value={formData.staff_type}
                    onChange={(e) => handleInputChange('staff_type', e.target.value)}
                    disabled={saving}
                  >
                    <option value="teaching">Teaching</option>
                    <option value="non_teaching">Non-Teaching</option>
                    <option value="admin">Administrative</option>
                  </select>
                ) : (
                  <span className="value capitalize">{staff.staff_type?.replace('_', ' ') || '-'}</span>
                )}
              </div>
              <div className={`info-row ${editing ? 'editing' : ''}`}>
                <span className="label">Position:</span>
                {editing ? (
                  <input
                    type="text"
                    className="form-input-sm"
                    value={formData.position}
                    onChange={(e) => handleInputChange('position', e.target.value)}
                    placeholder="e.g., Mathematics Teacher"
                    disabled={saving}
                  />
                ) : (
                  <span className="value">{staff.position || '-'}</span>
                )}
              </div>
              <div className={`info-row ${editing ? 'editing' : ''}`}>
                <span className="label">Department:</span>
                {editing ? (
                  <input
                    type="text"
                    className="form-input-sm"
                    value={formData.department}
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    placeholder="e.g., Science"
                    disabled={saving}
                  />
                ) : (
                  <span className="value">{staff.department || '-'}</span>
                )}
              </div>
              <div className={`info-row ${editing ? 'editing' : ''}`}>
                <span className="label">Manager:</span>
                {editing ? (
                  <select
                    className="form-input-sm"
                    value={formData.manager_id}
                    onChange={(e) => handleInputChange('manager_id', e.target.value)}
                    disabled={saving}
                  >
                    <option value="">No manager</option>
                    {managerOptions.filter(m => m.id !== staff.id).map(m => (
                      <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>
                    ))}
                  </select>
                ) : (
                  <span className="value">{staff.manager_name || '-'}</span>
                )}
              </div>
              <div className="info-row">
                <span className="label">Status:</span>
                <span className={`badge ${staff.status}`}>{staff.status}</span>
              </div>
              <div className={`info-row ${editing ? 'editing' : ''}`}>
                <span className="label">Date Joined:</span>
                {editing ? (
                  <input
                    type="date"
                    className="form-input-sm"
                    value={formData.date_joined}
                    onChange={(e) => handleInputChange('date_joined', e.target.value)}
                    disabled={saving}
                  />
                ) : (
                  <span className="value">{staff.date_joined || '-'}</span>
                )}
              </div>
            </div>
          </div>

          <div className="info-card">
            <h3>Contact Information</h3>
            <div className="info-rows">
              <div className={`info-row ${editing ? 'editing' : ''}`}>
                <span className="label">Personal Email:</span>
                {editing ? (
                  <input
                    type="email"
                    className="form-input-sm"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={saving}
                    required
                  />
                ) : (
                  <span className="value">{staff.email || '-'}</span>
                )}
              </div>
              <div className={`info-row ${editing ? 'editing' : ''}`}>
                <span className="label">Phone:</span>
                {editing ? (
                  <input
                    type="tel"
                    className="form-input-sm"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    disabled={saving}
                    required
                  />
                ) : (
                  <span className="value">{staff.phone || '-'}</span>
                )}
              </div>
              <div className={`info-row ${editing ? 'editing' : ''}`}>
                <span className="label">Qualification:</span>
                {editing ? (
                  <input
                    type="text"
                    className="form-input-sm"
                    value={formData.qualification}
                    onChange={(e) => handleInputChange('qualification', e.target.value)}
                    placeholder="e.g., Bachelor's in Education"
                    disabled={saving}
                  />
                ) : (
                  <span className="value">{staff.qualification || '-'}</span>
                )}
              </div>
            </div>
          </div>

          <div className="info-card">
            <h3>Portal Credentials</h3>
            <p style={{ fontSize: '13px', color: '#666', marginBottom: '16px' }}>
              These credentials are used to access the staff portal
            </p>
            <div className="info-rows">
              {staff.portal_account ? (
                <>
                  <div className="info-row">
                    <span className="label">Portal Email:</span>
                    <span className="value mono">{staff.portal_account.email}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">Portal Password:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="value mono">{showPassword ? staff.portal_account.password : '•'.repeat(staff.portal_account.password?.length || 8)}</span>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => setShowPassword(!showPassword)}
                        title={showPassword ? 'Hide password' : 'Show password'}
                        style={{ padding: '4px 8px', minWidth: 'auto' }}
                      >
                        {showPassword ? <EyeSlash size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                  <div className="info-row">
                    <span className="label"></span>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={handleRegenerateStaffCredentials}
                      disabled={regeneratingStaff}
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      {regeneratingStaff ? 'Regenerating...' : 'Regenerate Credentials'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="info-row">
                  <span className="label"></span>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleRegenerateStaffCredentials}
                    disabled={regeneratingStaff}
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                  >
                    {regeneratingStaff ? 'Generating...' : 'Generate Portal Credentials'}
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="info-card">
            <h3>Probation</h3>
            <div className="info-rows">
              <div className="info-row">
                <span className="label">Status:</span>
                <span className={`badge ${staff.probation_status || 'not_applicable'}`}>
                  {(staff.probation_status || 'not_applicable').replace('_', ' ')}
                </span>
              </div>
              {staff.probation_end_date && (
                <div className="info-row">
                  <span className="label">End Date:</span>
                  <span className="value">{staff.probation_end_date}</span>
                </div>
              )}
              {staff.probation_confirmed_at && (
                <div className="info-row">
                  <span className="label">Decided At:</span>
                  <span className="value">{new Date(staff.probation_confirmed_at).toLocaleDateString()}</span>
                </div>
              )}
              {(!staff.probation_status || staff.probation_status === 'not_applicable') && (
                <div className="info-row">
                  <span className="label"></span>
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={handleStartProbation}
                    disabled={probationSaving}
                    style={{ fontSize: '12px', padding: '6px 12px' }}
                  >
                    Start Probation
                  </button>
                </div>
              )}
              {staff.probation_status === 'on_probation' && (
                <div className="info-row">
                  <span className="label"></span>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-sm btn-success"
                      onClick={handleConfirmProbation}
                      disabled={probationSaving}
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    >
                      Confirm
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={handleExtendProbation}
                      disabled={probationSaving}
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    >
                      Extend
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={handleTerminateProbation}
                      disabled={probationSaving}
                      style={{ fontSize: '12px', padding: '6px 12px' }}
                    >
                      Terminate
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Teaching Assignments Section (only for teaching staff) */}
        {staff.staff_type === 'teaching' && staff.assignments && staff.assignments.length > 0 && (
          <div className="section assignments-section">
            <div className="section-header">
              <h2>Teaching Assignments</h2>
            </div>
            <div className="assignments-grid">
              {staff.assignments.map(assignment => (
                <div key={assignment.id} className="assignment-card">
                  <div className="assignment-header">
                    <h3>{assignment.subject_name}</h3>
                    {assignment.is_class_teacher && (
                      <span className="class-teacher-badge">Class Teacher</span>
                    )}
                  </div>
                  <div className="assignment-details">
                    <div className="detail-row">
                      <Building size={16} className="icon" />
                      <span>Class: {assignment.class_name}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Additional Information Section */}
        <div className="section additional-info-section">
          <div className="section-header">
            <h2>Additional Information</h2>
          </div>
          <div className="info-card">
            <div className="info-rows">
              <div className="info-row">
                <span className="label">Created At:</span>
                <span className="value">{new Date(staff.created_at).toLocaleDateString()}</span>
              </div>
              {staff.photo_url && (
                <div className="info-row">
                  <span className="label">Photo:</span>
                  <span className="value">
                    <img src={staff.photo_url} alt="Staff photo" className="staff-photo" />
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Documents Section */}
        <div className="section additional-info-section">
          <div className="section-header">
            <h2>Documents</h2>
          </div>
          <DocumentsPanel ownerType="staff" ownerId={staffId} canManage={true} />
        </div>

        {/* Dialog Modal */}
        <DialogModal
          isOpen={dialogConfig.isOpen}
          onClose={closeDialog}
          title={dialogConfig.title}
          message={dialogConfig.message}
          type={dialogConfig.type}
          primaryAction={dialogConfig.primaryAction}
          secondaryAction={dialogConfig.secondaryAction}
          input={dialogConfig.input}
        />
      </div>
    </Layout>
  );
}

export default StaffDetailPage;